import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { CoveragesByHourDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import WrapperCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/WrapperCell'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import Coverages from 'modules/shift_management/store/internal/collections/Coverages'

import { ColumnsType, gridColumn } from '../utils/buildColumns'
import EmployeesByHourGrid from './EmployeesByHourGrid'

interface Props {
  columns: ColumnsType
}

const EmployeesByHourRow = ({ columns }: Props) => {
  const [filters] = useFilters()
  const [{ view, newShiftsToQuery }] = useSchedule()

  const employeeIds = filters.employee_ids?.map((employeeId) => Number(employeeId))
  const cells = columns.slice(1)
  const [open, toggle] = useToggle(false)
  const title = i18n.t('shift_management.employees_by_hour.label')
  const tooltip = i18n.t('shift_management.employees_by_hour.tooltip')

  const { data, refetch } = useQuery(CoveragesByHourDocument, {
    variables: {
      startAt: view.startAt,
      endAt: view.endAt,
      customFilters: filters.custom_filters,
      logicOperator: filters.logic_operator,
      employeeIds: employeeIds,
      terminated: filters.terminated,
      shiftIds: filters.shift_ids,
    },
  })

  const coveragesByHour: Coverages = useUnsafeMemo(
    () => Coverages.buildFromRequest(data?.shiftManagement?.coveragesConnection.nodes || []),
    [data?.shiftManagement?.coveragesConnection]
  )

  useUnsafeEffect(() => {
    refetch()
  }, [refetch, newShiftsToQuery])

  return (
    <>
      <WrapperCell sticky minWidth="s240" height="s24">
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          height="full"
          paddingX="s8"
          alignItems="center"
        >
          <Tooltip.ItemList attachment="top center" open={open} toggle={toggle}>
            <Box flexDirection="row" alignItems="center" overflowX="hidden">
              <Text size="100" weight="bold" color="grey700" ellipsis>
                {title.toUpperCase()}
              </Text>
            </Box>

            <Box alignItems="center">
              <Text.Normal>{tooltip}</Text.Normal>
            </Box>
          </Tooltip.ItemList>
        </Box>
      </WrapperCell>
      <Box style={{ ...gridColumn(1, columns.length) }}>
        <WrapperCell minWidth="full" height="s24">
          <EmployeesByHourGrid coverages={coveragesByHour} view={view} columns={cells} />
        </WrapperCell>
      </Box>
    </>
  )
}

export default observer(EmployeesByHourRow)
